import { Badge } from "@/components/badge";
import { Button } from "@/components/button";
import { Card } from "@/components/card";
import { cn } from "@/lib/utils";
import type {
  ActionNode as ActionNodeType,
  DocumentNode as DocumentNodeType,
  EmailNode as EmailNodeType,
  ExtractionNode as ExtractionNodeType,
} from "@repo/shared/workflow";
import { Handle, type NodeProps, Position, useNodesData } from "@xyflow/react";
import { ChevronDown, File } from "lucide-react";
import type { FC } from "react";
import { type RFNode, useEditorState } from "../state";

const NodeCard: React.FC<{ children: React.ReactNode; className?: string }> = ({
  children,
  className = "",
}) => {
  return (
    <Card className={`p-3 min-w-[180px] shadow-md border-2 ${className}`}>
      {children}
    </Card>
  );
};

export const DocumentNode: FC<{ node: DocumentNodeType }> = ({ node }) => {
  return (
    <div className="flex flex-col gap-2 py-1">
      <Badge variant="outline" className="bg-blue-50 text-blue-600 w-fit p-1.5">
        <File className="w-4 h-4" />
      </Badge>
      <div className="font-semibold break-words">{node.name}</div>
    </div>
  );
};

export const ExtractionNode: FC<{ node: ExtractionNodeType }> = ({ node }) => {
  return (
    <div className="flex flex-col gap-2 py-1">
      <Badge variant="outline" className="bg-green-50 text-green-600 w-fit">
        Extraction
      </Badge>
      <div className="font-semibold break-words">{node.name}</div>
    </div>
  );
};

export const RuleNode: FC<{ node: ExtractionNodeType }> = ({ node }) => {
  return (
    <div className="flex flex-col gap-2 py-1">
      <Badge variant="outline" className="bg-purple-50 text-purple-600 w-fit">
        Rule
      </Badge>
      <div className="font-semibold break-words">{node.name}</div>
    </div>
  );
};

export const EmailNode: FC<{ node: EmailNodeType }> = ({ node }) => {
  return (
    <div className="flex flex-col gap-2 py-1">
      <Badge variant="outline" className="bg-indigo-50 text-indigo-600 w-fit">
        Email
      </Badge>
      <div className="font-semibold break-words">{node.name}</div>
    </div>
  );
};

// Action Node
export const ActionNode: FC<{ node: ActionNodeType }> = ({ node }) => {
  let badgeColor: string;
  switch (node.subType) {
    case "export":
      badgeColor = "bg-purple-50 text-purple-600";
      break;
    case "send-email":
      badgeColor = "bg-indigo-50 text-indigo-600";
      break;
    case "rpa":
      badgeColor = "bg-orange-50 text-orange-600";
      break;
    case "rest":
      badgeColor = "bg-yellow-50 text-yellow-600";
      break;
    default:
      badgeColor = "bg-gray-50 text-gray-600";
  }

  return (
    <div className="flex flex-col gap-2 py-1">
      <Badge variant="outline" className={`${badgeColor} w-fit`}>
        {node.subType} Action
      </Badge>
      <div className="font-semibold break-words">{node.name}</div>
    </div>
  );
};

export const GroupNode: FC<NodeProps<RFNode>> = ({
  id,
  data,
  selected,
  isConnectable,
}) => {
  const node = data.node;
  const children = useNodesData<RFNode>(node.dependencyIds);
  const nodeCollapsed = useEditorState((state) => state.collapsed[id]);
  const collapsed = useEditorState((state) => state.collapsed);
  const selectNode = useEditorState((state) => state.selectNode);
  const setNodeCollapsed = useEditorState((state) => state.setNodeCollapsed);

  return (
    <div
      className={
        "min-w-[180px] rounded-xl bg-background overflow-hidden shadow-md border-2"
      }
    >
      <Handle
        type="target"
        position={Position.Top}
        isConnectable={isConnectable}
      />
      <div className="p-2 px-3 bg-gray-50 flex items-center justify-between cursor-pointer">
        <div className="font-semibold break-words">{data.displayName}</div>
        <Button
          size="icon"
          variant="ghost"
          onClick={() => {
            setNodeCollapsed(id, !nodeCollapsed);
          }}
        >
          <ChevronDown
            className={cn("w-4 h-4 transition-transform duration-200", {
              "transform rotate-180": nodeCollapsed,
            })}
          />
        </Button>
      </div>
      {!nodeCollapsed && (
        <div className="flex flex-col px-3 py-2 gap-1">
          {children.map((child) => (
            <div
              className="px-1.5 py-2 border rounded cursor-pointer hover:bg-gray-100"
              key={child.id}
              onClick={(e) => {
                e.stopPropagation();
                selectNode(child.id);
              }}
            >
              <div className="font-medium break-words px-1">
                {child.data.displayName}
              </div>
            </div>
          ))}
        </div>
      )}
      <Handle
        type="source"
        position={Position.Bottom}
        isConnectable={isConnectable}
      />
    </div>
  );
};

export const BaseNode: FC<NodeProps<RFNode>> = (props) => {
  const {
    data: { node },
    selected,
    isConnectable,
  } = props;

  return (
    <NodeCard className={selected ? "border-blue-500" : "border-blue-200"}>
      <Handle
        type="target"
        position={Position.Top}
        isConnectable={isConnectable}
      />
      {node.subType === "document" && (
        <DocumentNode node={node as DocumentNodeType} />
      )}
      {node.subType === "extraction" && !node.showDescription && (
        <ExtractionNode node={node as ExtractionNodeType} />
      )}
      {node.subType === "extraction" && node.showDescription && (
        <RuleNode node={node as ExtractionNodeType} />
      )}
      {node.type === "entity" && node.subType === "email" && (
        <EmailNode node={node as EmailNodeType} />
      )}
      {node.type === "action" && <ActionNode node={node as ActionNodeType} />}
      <Handle
        type="source"
        position={Position.Bottom}
        isConnectable={isConnectable}
      />
    </NodeCard>
  );
};

export const FoundationNodeTypes = {
  base: BaseNode,
  "foundation-group": GroupNode,
};
